<template>
    <div>
        <breadcrumb :items="breadcrumbs" />
        <v-container fluid>
            <div class="d-flex">
                <h2 class="font-weight-bolder mb-6">{{ isEditMode ? $t('finance.loanPrograms.edit') : $t('finance.loanPrograms.create') }}</h2>
                <v-spacer></v-spacer>
                <div class="d-flex align-center">
                    <v-btn outlined class="mr-3" @click="handleCancel">
                        {{ $t('Cancel') }}
                    </v-btn>
                    <v-btn v-if="currentStep === 1 && !isEditMode" color="primary" dark @click="handleSubmit" :disabled="!isFormValid">
                        {{ $t('Next') }}
                    </v-btn>
                    <!-- step 2 button: Update when edit, Create when create -->
                    <v-btn v-if="currentStep === 2" color="primary" dark @click="handleCreate">
                        {{ isEditMode ? $t('Update') : $t('Create') }}
                    </v-btn>
                    <v-btn v-if="isEditMode" color="primary" dark @click="handleCreate">
                        {{ $t('Save') }}
                    </v-btn>
                </div>
            </div>
            <!-- hide if is edit mode  enable -->
            <CustomStepper v-if="!isEditMode" :currentStep="currentStep" :steps="tabs" @step-changed="handleStepChange" class="mb-6" />

            <!-- Step 1: Program Details (v-show keeps ref alive for validation when on step 2) -->
            <div v-show="isEditMode || currentStep === 1">
                <StepOne
                    ref="step1Form"
                    v-model="step1Valid"
                    :form-data="formData"
                    :existing-logo-url="formData.existingLogoUrl"
                    :data-fields="programDetailDataFields"
                    :bank-options="bankOptions"
                    :currency-options="currencyOptions"
                    @add-data-field="addDataField('programDetail')"
                    @edit-data-field="(index) => editDataField(index, 'programDetail')"
                    @remove-data-field="(index) => removeDataField(index, 'programDetail')"
                />
            </div>

            <!-- Step 2: Application Form (v-show keeps ref alive when navigating back) -->
            <div v-show="!isEditMode && currentStep === 2">
                <StepTwo
                    ref="step2Form"
                    v-model="step2Valid"
                    :data-fields="applicationFormDataFields"
                    @add-data-field="addDataField('applicationForm')"
                    @edit-data-field="(index) => editDataField(index, 'applicationForm')"
                    @remove-data-field="(index) => removeDataField(index, 'applicationForm')"
                    @remove-group-field="(parentIndex, childIndex) => removeGroupField(parentIndex, childIndex, 'applicationForm')"
                />
            </div>

            <!-- Navigation Buttons -->
            <div class="d-flex justify-space-between mt-6">
                <v-btn v-if="currentStep > 1 && !isEditMode" outlined @click="previousStep">
                    {{ $t('Back') }}
                </v-btn>
                <div></div>
                <v-btn v-if="currentStep < totalSteps && !isEditMode" color="primary" dark @click="nextStep">
                    {{ $t('Next') }}
                </v-btn>
            </div>
        </v-container>

        <!-- Add Data Field Dialog -->
        <AddDataField :dialog="showAddDataFieldDialog" :edit-field="editingField" @close="closeAddDataFieldDialog"
            @submit="handleDataFieldSubmit" />

        <!-- Preview Form Drawer -->
        <PreviewForm v-model="showPreviewForm" :data-fields="applicationFormDataFields" />
    </div>
</template>

<script>
import CustomStepper from '../../esg-platform/report-builder/components/CustomStepperTab.vue';
import AddDataField from './components/AddDataField.vue';
import StepOne from './components/StepOne.vue';
import StepTwo from './components/StepTwo.vue';
import PreviewForm from './components/PreviewForm.vue';
import FinanceService from '@/_services/FinanceService';
import loadingMixin from '@/mixins/LoadingMixin';
import { currencies } from '../../marketplace/constants/currency';
import {
    BACKEND_FILE_FIELD_TYPES,
    normalizeFieldType,
    getChoiceOptions,
    parseNumericFieldValue,
    getMultipartFileForDynamicField,
    hasMandatoryDynamicFileProvided,
    processDataFields,
    transformApiFieldsToForm,
} from '@/utils/loanProgramFieldPayload';

export default {
    name: 'CreateProgram',
    components: {
        CustomStepper,
        AddDataField,
        StepOne,
        StepTwo,
        PreviewForm,
    },
    data() {
        return {
            currentStep: 1,
            totalSteps: 2,
            step1Valid: false,
            step2Valid: false,
            breadcrumbs: [
                { text: this.$t('Dashboard'), disabled: false, to: '/' },
                { text: this.$t('finance.loanPrograms.title'), disabled: false, to: { name: 'LoanProgram' } },
                { text: this.$t('finance.loanPrograms.create'), disabled: true },
            ],
            tabs: [
                {
                    number: 1,
                    title: this.$t('finance.loanPrograms.step1ProgramDetails'),
                },
                {
                    number: 2,
                    title: this.$t('finance.loanPrograms.step2ApplicationForm'),
                },
            ],
            formData: {
                programTitle: '',
                bank: null,
                applicationApproval: 'COOPERATIVE',
                currency: null,
                minLoanAmount: '',
                maxLoanAmount: '',
                loanTerm: '',
                loanTermUnit: 'month',
                status: 'active',
                programImage: null,
                existingLogoUrl: null,
                description: '',
                settingsConclude: false,
                settingsAllowBank: false,
                yield: '',
                interestRate: '',
                typeOfFinancing: '',
                gracePeriod: '',
                gracePeriodUnit: 'month',
                crops: [],
                country: null,
                regions: null,
                conditionsRequirements: '',
            },
            bankOptions: [],
            currencyOptions: currencies.map(currency => ({ name: currency.currency, value: currency.code })),
            showAddDataFieldDialog: false,
            showPreviewForm: false,
            programDetailDataFields: [],
            applicationFormDataFields: [],
            editingField: null,
            editingFieldIndex: null,
            dataFieldTarget: null,
        };
    },
    computed: {
        programId() {
            return this.$route.params.id || null;
        },
        isEditMode() {
            return !!this.programId;
        },
        isFormValid() {
            if (this.currentStep === 1) {
                return this.step1Valid;
            }
            return this.step1Valid && this.step2Valid;
        },
    },
    watch: {
        programId: {
            handler(id) {
                const last = this.breadcrumbs[this.breadcrumbs.length - 1];
                if (last) {
                    last.text = id ? this.$t('finance.loanPrograms.edit') : this.$t('finance.loanPrograms.create');
                }
            },
            immediate: true,
        },
    },
    mounted() {
        this.programDetailDataFields.forEach(f => this.ensureFieldHasValue(f));
        this.loadBankOptions();
        if (this.programId) {
            this.loadProgramForEdit();
        }
    },
    methods: {
    async loadBankOptions() {
      try {
        const response = await FinanceService.getBanks({});
        const banks = response.data?.rows || [];
        this.bankOptions = Array.isArray(banks)
          ? banks.map(bank => ({ name: bank.name, value: bank.id }))
          : [];
      } catch (error) {
        console.error('Error fetching bank options:', error);
        this.bankOptions = [];
      }
    },
        handleStepChange(stepNumber) {
            if (stepNumber === 1) {
                this.currentStep = 1;
            } else if (stepNumber === 2) {
                if (this.validateStep1OrNotify()) {
                    this.currentStep = 2;
                }
            }
        },
        /**
         * Validates Step 1 (v-form + program detail dynamic fields in create mode).
         * Shows a notification when validation fails so the user cannot reach step 2 silently.
         */
        validateStep1OrNotify() {
            const step1 = this.$refs.step1Form;
            if (step1 && !step1.validate()) {
                this.$notify({
                    type: 'error',
                    text: this.$t('finance.loanPrograms.fillRequiredFields'),
                });
                return false;
            }
            if (!this.isEditMode) {
                const detailErrors = this.validateProgramDetailDataFieldsForSubmit();
                if (detailErrors.length) {
                    this.$notify({
                        type: 'error',
                        text: detailErrors[0],
                    });
                    return false;
                }
            }
            return true;
        },
        nextStep() {
            if (this.currentStep === 1 && this.validateStep1OrNotify()) {
                this.currentStep = 2;
            }
        },
        async loadProgramForEdit() {
            if (!this.programId) return;
            this.startLoading();
            try {
                const response = await FinanceService.getLoanProgramById(this.programId);
                const d = response?.data != null ? response.data : response;
                if (!d) return;

                const unitFromApi = (u) => ({ m: 'month', y: 'year' }[u] || 'month');

                this.formData.programTitle = d.programTitle || '';
                this.formData.bank = d.bank?.id ?? null;
                this.formData.applicationApproval = String(d.applicationApproval || 'COOPERATIVE').toUpperCase();
                this.formData.currency = d.loanCurrency || null;
                this.formData.minLoanAmount = d.loanMinAmt != null ? String(d.loanMinAmt) : '';
                this.formData.maxLoanAmount = d.loanMaxAmt != null ? String(d.loanMaxAmt) : '';
                this.formData.loanTerm = d.loanTerm != null ? String(d.loanTerm) : '';
                this.formData.loanTermUnit = unitFromApi(d.loanTermUnit);
                this.formData.status = d.status || 'active';
                this.formData.existingLogoUrl = d.programLogo || null;
                this.formData.description = d.description || '';
                this.formData.settingsConclude = !!d.settingsConclude;
                this.formData.settingsAllowBank = !!d.settingsAllowBank;
                this.formData.yield = d.loanYield != null ? String(d.loanYield).replace(/%/g, '') : '';
                this.formData.interestRate = d.interestRate != null
                    ? String(d.interestRate).replace(/%/g, '')
                    : (d.loanYield != null ? String(d.loanYield).replace(/%/g, '') : '');
                this.formData.typeOfFinancing = d.loanFinancingType || '';
                this.formData.gracePeriod = d.loanGracePeriod != null ? String(d.loanGracePeriod) : '';
                this.formData.gracePeriodUnit = unitFromApi(d.loanGracePeriodUnit);
                this.formData.crops = typeof d.loanCrops === 'string' ? (d.loanCrops ? d.loanCrops.split(',').map(s => s.trim()) : []) : (Array.isArray(d.loanCrops) ? d.loanCrops : []);
                this.formData.country = typeof d.loanCountry === 'object' && d.loanCountry ? d.loanCountry : (d.loanCountry ? { name: d.loanCountry } : null);
                this.formData.regions = typeof d.loanRegion === 'string'
                    ? (d.loanRegion
                        ? d.loanRegion.split(',').map(s => s.trim()).filter(Boolean)[0] || null
                        : null)
                    : (Array.isArray(d.loanRegion)
                        ? (d.loanRegion[0] || null)
                        : (d.loanRegion || null));
                this.formData.conditionsRequirements = d.conditionsRequirements || '';

                this.programDetailDataFields = this.transformApiFieldsToForm(d.dataFields || [], true);
                this.applicationFormDataFields = this.transformApiFieldsToForm(d.applicationFormFields || [], false);

                this.programDetailDataFields.forEach(f => this.ensureFieldHasValue(f));
            } catch (err) {
                console.error('Failed to load loan program for edit:', err);
                this.$notify({ type: 'error', text: this.$t('finance.loanPrograms.failedLoadLoanProgram') });
            } finally {
                this.stopLoading();
            }
        },
        async handleCreate() {
            if (this.$refs.step1Form && !this.$refs.step1Form.validate()) {
                this.$notify({
                    type: 'error',
                    text: this.$t('finance.loanPrograms.fillRequiredFields'),
                });
                return;
            }
            if (this.$refs.step2Form && !this.$refs.step2Form.validate()) {
                this.$notify({
                    type: 'error',
                    text: this.$t('finance.loanPrograms.fillRequiredFields'),
                });
                return;
            }
            if (!this.isEditMode) {
                const detailErrors = this.validateProgramDetailDataFieldsForSubmit();
                if (detailErrors.length) {
                    this.$notify({
                        type: 'error',
                        text: detailErrors[0],
                    });
                    return;
                }
            }
            this.startLoading();
            try {
                const fd = new FormData();

                if (this.formData.programTitle) fd.append('programTitle', this.formData.programTitle);
                if (this.formData.programImage instanceof File) {
                    fd.append('programLogo', this.formData.programImage);
                }
                if (this.formData.status) fd.append('status', this.formData.status);
                if (this.formData.bank) fd.append('bankId', this.formData.bank);
                const applicationApproval = String(this.formData.applicationApproval || 'COOPERATIVE').toUpperCase();
                fd.append('applicationApproval', applicationApproval);
                if (typeof this.formData.settingsConclude === 'boolean') fd.append('settingsConclude', this.formData.settingsConclude);
                if (typeof this.formData.settingsAllowBank === 'boolean') fd.append('settingsAllowBank', this.formData.settingsAllowBank);
                if (this.formData.currency) fd.append('loanCurrency', this.formData.currency);
                if (this.formData.minLoanAmount) fd.append('loanMinAmt', this.formData.minLoanAmount);
                if (this.formData.maxLoanAmount) fd.append('loanMaxAmt', this.formData.maxLoanAmount);
                if (this.formData.loanTerm) fd.append('loanTerm', this.formData.loanTerm);
                if (this.formData.loanTermUnit) {
                    const unitMap = { month: 'm', year: 'y' };
                    fd.append('loanTermUnit', unitMap[this.formData.loanTermUnit] || this.formData.loanTermUnit);
                }
                if (applicationApproval === 'BANK') {
                    const rate = this.formData.interestRate != null && String(this.formData.interestRate).trim() !== ''
                        ? this.formData.interestRate
                        : this.formData.yield;
                    if (rate) {
                        fd.append('interestRate', rate);
                    }
                } else if (this.formData.yield) {
                    fd.append('loanYield', this.formData.yield);
                }
                if (this.formData.typeOfFinancing) fd.append('loanFinancingType', this.formData.typeOfFinancing);
                if (this.formData.gracePeriod) fd.append('loanGracePeriod', this.formData.gracePeriod);
                if (this.formData.gracePeriodUnit) {
                    const unitMap = { month: 'm', year: 'y' };
                    fd.append('loanGracePeriodUnit', unitMap[this.formData.gracePeriodUnit] || this.formData.gracePeriodUnit);
                }
                
                if (Array.isArray(this.formData.crops) ? this.formData.crops.length : this.formData.crops) {
                    fd.append('loanCrops', Array.isArray(this.formData.crops) ? this.formData.crops.join(',') : this.formData.crops);
                }
                if (this.formData.country) {
                    fd.append('loanCountry', this.formData.country.name || this.formData.country);
                }
                if (Array.isArray(this.formData.regions) ? this.formData.regions.length : this.formData.regions) {
                    fd.append('loanRegion', Array.isArray(this.formData.regions) ? this.formData.regions.join(',') : this.formData.regions);
                }
                if (this.formData.description) fd.append('description', this.formData.description);
                if (this.formData.conditionsRequirements) {
                    fd.append('conditionsRequirements', this.formData.conditionsRequirements);
                }

                const dataResult = this.processDataFields(this.programDetailDataFields, { includeValue: true });
                const appResult = this.processDataFields(this.applicationFormDataFields, { includeValue: false });

                fd.append('dataFields', JSON.stringify(dataResult.flatFields));
                fd.append('applicationFields', JSON.stringify(appResult.flatFields));
                fd.append('groupedDataFields', JSON.stringify(dataResult.groupedFields));
                fd.append('groupedApplicationFields', JSON.stringify(appResult.groupedFields));

                const fileFieldNames = [];
                dataResult.fileFields.forEach(({ name, file }) => {
                    const files = Array.isArray(file) ? file : [file];
                    files.forEach((f) => {
                        if (f) fd.append(name, f);
                    });
                    if (files.some(Boolean)) fileFieldNames.push(name);
                });

                const params = fileFieldNames.length
                    ? { fileFields: fileFieldNames.join(',') }
                    : {};

                if (this.isEditMode) {
                    await FinanceService.updateLoanProgram(this.programId, fd, params);
                    this.$notify({ type: 'success', text: this.$t('finance.loanPrograms.loanProgramUpdatedSuccess') });
                } else {
                    await FinanceService.createLoanProgram(fd, params);
                    this.$notify({ type: 'success', text: this.$t('finance.loanPrograms.loanProgramCreatedSuccess') });
                }
                this.$router.push({ name: 'loanPrograms' });
            } catch (error) {
                const backendMessage =
                    error?.data?.msg ||
                    error?.data?.message ||
                    error?.response?.data?.msg ||
                    error?.response?.data?.message ||
                    null;
                this.$notify({
                    type: 'error',
                    text: backendMessage || (this.isEditMode
                        ? this.$t('finance.loanPrograms.failedUpdateLoanProgram')
                        : this.$t('finance.loanPrograms.failedCreateLoanProgram')),
                });
            } finally {
                this.stopLoading();
            }
        },
        previousStep() {
            if (this.currentStep > 1) {
                this.currentStep--;
            }
        },
        headerClass(stepNumber) {
            return stepNumber === this.currentStep
                ? 'active-header-navigation'
                : 'header-navigation';
        },
        handleCancel() {
            this.$router.push({ name: 'loanPrograms' });
        },
        addDataField(target) {
            this.dataFieldTarget = target;
            this.editingField = null;
            this.editingFieldIndex = null;
            this.showAddDataFieldDialog = true;
        },
        closeAddDataFieldDialog() {
            this.showAddDataFieldDialog = false;
            this.editingField = null;
            this.editingFieldIndex = null;
            this.dataFieldTarget = null;
        },
        getDataFieldsList(target) {
            return target === 'applicationForm' ? this.applicationFormDataFields : this.programDetailDataFields;
        },
        handleDataFieldSubmit(fieldData) {
            const target = this.dataFieldTarget || 'programDetail';
            const list = this.getDataFieldsList(target);
            const isEdit = this.editingFieldIndex !== null;
            const existingField = isEdit ? list[this.editingFieldIndex] : null;
            
            if (fieldData.isGroup && fieldData.groupFields) {
                fieldData.groupFields.forEach((subField, subIndex) => {
                    // Preserve existing values if editing
                    if (isEdit && existingField && existingField.groupFields && existingField.groupFields[subIndex]) {
                        const existingSubField = existingField.groupFields[subIndex];
                        if (existingSubField.value !== undefined && existingSubField.value !== null) {
                            subField.value = existingSubField.value;
                        }
                    }
                    // Initialize value based on data type
                    if (subField.value === undefined || subField.value === null) {
                        if (subField.dataType === 'check_boxes') {
                            this.$set(subField, 'value', []);
                        } else {
                            this.$set(subField, 'value', '');
                        }
                    }
                    // Initialize date menu
                    if (subField.dataType === 'date_field') {
                        this.$set(subField, '_dateMenu', false);
                    }
                });
            } else {
                // Handle single fields
                const value = isEdit && existingField && (existingField.value !== undefined && existingField.value !== null)
                    ? existingField.value
                    : this.getDefaultFieldValue(fieldData);
                if (fieldData.dataType === 'date_field') {
                    this.$set(fieldData, '_dateMenu', false);
                }
                this.$set(fieldData, 'value', value);
            }
            
            if (!fieldData._uid) {
                this.$set(fieldData, '_uid', 'field-' + Date.now() + '-' + Math.random().toString(36).slice(2));
            }
            if (isEdit) {
                this.$set(list, this.editingFieldIndex, fieldData);
            } else {
                list.push(fieldData);
            }
            if (target === 'programDetail') {
                this.ensureFieldHasValue(fieldData);
            }
            this.closeAddDataFieldDialog();
        },
        editDataField(index, target) {
            const list = this.getDataFieldsList(target);
            this.dataFieldTarget = target;
            this.editingFieldIndex = index;
            this.editingField = JSON.parse(JSON.stringify(list[index]));
            this.showAddDataFieldDialog = true;
        },
        removeDataField(index, target) {
            const list = this.getDataFieldsList(target);
            list.splice(index, 1);
        },
        removeGroupField(parentIndex, childIndex, target) {
            const list = this.getDataFieldsList(target);
            const group = list[parentIndex];
            if (group && group.isGroup && group.groupFields && group.groupFields.length > childIndex) {
                group.groupFields.splice(childIndex, 1);
            }
        },
        getDefaultFieldValue(field) {
            if (!field || field.isGroup) return null;
            switch (field.dataType) {
                case 'check_boxes':
                    return field.value && Array.isArray(field.value) ? field.value : [];
                default:
                    return field.value !== undefined && field.value !== null ? field.value : '';
            }
        },
        ensureFieldHasValue(field) {
            if (field.isGroup) {
                // For group fields, ensure each subfield has a value
                if (field.groupFields && field.groupFields.length > 0) {
                    field.groupFields.forEach(subField => {
                        this.ensureSubFieldHasValue(subField);
                    });
                }
                return;
            }
            if (field.value === undefined || (field.dataType === 'check_boxes' && !Array.isArray(field.value))) {
                this.$set(field, 'value', this.getDefaultFieldValue(field));
            }
            if (field.dataType === 'date_field' && !Object.prototype.hasOwnProperty.call(field, '_dateMenu')) {
                this.$set(field, '_dateMenu', false);
            }
        },
        ensureSubFieldHasValue(subField) {
            if (subField.dataType === 'check_boxes') {
                if (!subField.value || !Array.isArray(subField.value)) {
                    this.$set(subField, 'value', []);
                }
            } else if (subField.value === undefined || subField.value === null) {
                this.$set(subField, 'value', '');
            }
            if (subField.dataType === 'date_field' && !Object.prototype.hasOwnProperty.call(subField, '_dateMenu')) {
                this.$set(subField, '_dateMenu', false);
            }
        },
        normalizeFieldType,
        getChoiceOptions,
        parseNumericFieldValue,
        getMultipartFileForDynamicField,
        processDataFields,
        transformApiFieldsToForm,
        validateProgramDetailDataFieldsForSubmit() {
            const errors = [];
            const walk = (f, pathLabel) => {
                const label = f.fieldTitle || pathLabel;
                const t = this.normalizeFieldType(f.dataType);
                const mandatory = !!(f.isMandetory ?? f.mandatory ?? f.isMandatory);
                if (BACKEND_FILE_FIELD_TYPES.includes(t)) {
                    if (mandatory && !hasMandatoryDynamicFileProvided(f)) {
                        errors.push(`${this.$t('finance.loanPrograms.fileUploadRequiredFor')}: ${label}`);
                    }
                    return;
                }
                if (!mandatory) return;
                if (t === 'check_boxes') {
                    if (!Array.isArray(f.value) || f.value.length === 0) {
                        errors.push(`${this.$t('finance.loanPrograms.selectAtLeastOneOptionFor')}: ${label}`);
                    }
                    return;
                }
                if (t === 'dropdown' && f.allowMultipleInputs) {
                    if (!Array.isArray(f.value) || f.value.length === 0) {
                        errors.push(`${this.$t('finance.loanPrograms.selectAtLeastOneOptionFor')}: ${label}`);
                    }
                    return;
                }
                if (t === 'dropdown' && !f.allowMultipleInputs) {
                    if (f.value === undefined || f.value === null || f.value === '') {
                        errors.push(`${this.$t('finance.loanPrograms.fieldRequired')}: ${label}`);
                    }
                    return;
                }
                if (t === 'radio_button') {
                    const choices = this.getChoiceOptions(f);
                    const v = f.value;
                    if (v === undefined || v === null || v === '' || !choices.includes(v)) {
                        errors.push(`${this.$t('finance.loanPrograms.chooseValidOptionFor')}: ${label}`);
                    }
                    return;
                }
                if (t === 'measurement_unit') {
                    const n = this.parseNumericFieldValue(f.value);
                    if (n === null) {
                        errors.push(`${this.$t('finance.loanPrograms.enterValidAmountFor')}: ${label}`);
                        return;
                    }
                    const units = Array.isArray(f.units) && f.units.length
                        ? f.units
                        : (f.measurementType ? [f.measurementType] : []);
                    const unit = f.measurementType || units[0] || '';
                    if (units.length && unit && !units.includes(unit)) {
                        errors.push(`${this.$t('finance.loanPrograms.selectValidUnitFor')}: ${label}`);
                    }
                    return;
                }
                if (f.value === undefined || f.value === null || f.value === '') {
                    errors.push(`${this.$t('finance.loanPrograms.fieldRequired')}: ${label}`);
                }
            };
            (this.programDetailDataFields || []).forEach((field, i) => {
                if (field.isGroup) {
                    (field.groupFields || []).forEach((sub, j) => walk(sub, `${i}.${j}`));
                } else {
                    walk(field, String(i));
                }
            });
            return errors;
        },
        async handleSubmit() {
            if (this.currentStep === 1) {
                if (!this.validateStep1OrNotify()) {
                    return;
                }
                this.currentStep = 2;
                return;
            }

            let isValid = true;
            if (this.$refs.step1Form) {
                isValid = this.$refs.step1Form.validate();
            }
            if (isValid && this.$refs.step2Form) {
                isValid = this.$refs.step2Form.validate();
            }
            if (!isValid) {
                this.$notify({
                    type: 'error',
                    text: this.$t('finance.loanPrograms.fillRequiredFields'),
                });
            }
        },
    },
    mixins: [loadingMixin],
};
</script>

<style scoped>
.tabs-container {
    display: flex;
    overflow-x: auto;
    white-space: nowrap;
    width: 100%;
    margin-bottom: 24px;
}

.tab-header {
    cursor: pointer;
    align-items: center;
}

.tab-header .next-tab-arrow,
.tab-header .headline-text-color {
    display: flex;
    justify-content: center;
    align-items: center;
}

.next-tab-arrow {
    margin-left: 20px;
    margin-right: 10px;
    font-size: 30px;
    font-weight: 500;
    color: #4b4b4b;
}

.active-header-navigation .headline-text-color {
    color: var(--primary-color) !important;
}

.headline-text-color {
    color: #4b4b4b !important;
    font-weight: 500;
}

.header-navigation .square {
    width: 35px;
    height: 35px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 5px;
    background-color: #d2d2d2;
    margin: 10px;
}

.active-header-navigation .square {
    width: 35px;
    height: 35px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 5px;
    background-color: var(--primary-color);
    margin: 10px;
}

.number {
    font-size: 20px;
    font-weight: 400;
    color: #4b4b4b;
}

.active-header-navigation .number {
    font-size: 20px;
    font-weight: 400;
    color: #ffffff;
}

label {
    font-size: 14px;
    color: #333;
}

.image-upload-container {
    width: 100%;
}

.image-upload-dropzone {
    border: 2px dashed #d3d3d3;
    border-radius: 4px;
    padding: 60px 20px;
    text-align: center;
    cursor: pointer;
    background-color: #ffffff;
    transition: border-color 0.3s ease;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 200px;
}

.image-upload-dropzone:hover {
    border-color: #9e9e9e;
}

.camera-icon {
    margin-bottom: 20px;
    opacity: 0.6;
}

.add-image-btn {
    margin-top: 10px;
    text-transform: none;
    font-weight: 500;
}

.data-field-item {
    padding: 12px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.data-field-item:last-child {
    border-bottom: none;
}
.data-field-label {
    font-size: 1rem;
    color: #333;
}
.data-field-input-wrap >>> .v-input__slot {
    margin-bottom: 0;
}
</style>
